(ns ui.components.date-picker.view
    (:require
     [clojure.edn :as edn]
     [common.dates :as dates]
     [common.localization :as loc]
     [common.util :as cu]
     [frontend.dom-utils :as du]
     ["date-fns" :as date-fns]
     ["date-fns/locale" :as date-fns-locale]
     [react :as react]
     [reagent.core :as r]
     [ui.components.button.view :as button]
     ["react-day-picker" :as ReactDayPicker]
     ["@floating-ui/react" :refer
      [useFloating
       autoUpdate
       offset
       flip
       shift
       useDismiss
       useRole
       useClick
       useInteractions
       FloatingFocusManager
       FloatingPortal]]
     [ui.components.select.select :as select]
     [ui.dom-globals :as dg]
     [ui.util :as uu]))

; Todo date formatting, replace date picker everywhere, test that it works

(defn adapt
  ([cls-name]
   (r/adapt-react-class (aget ReactDayPicker cls-name)))
  ([parent-cls cls-name]
   (r/adapt-react-class (aget (aget ReactDayPicker parent-cls) cls-name))))

(def react-day-picker (adapt "DayPicker"))

(defn locale-week-starts-on
  "Returns date-fns/react-day-picker week start, where Sunday is 0."
  []
  (try
    (let [language (or (some-> js/navigator .-language)
                       "en")
          first-day (some-> (js/Intl.Locale. language)
                            .-weekInfo
                            .-firstDay)]
      (cond
        (= 7 first-day) 0
        (contains? #{1 2 3 4 5 6} first-day) first-day
        :else 1))
    (catch js/Object _err
      1)))

(defn default-min-date
  []
  (js/Date. 1000 1 1))

(defn default-max-date
  []
  (js/Date. 3000 11 31 23 59))

(def month-options
  [{:value 0
    :label "January"}
   {:value 1
    :label "February"}
   {:value 2
    :label "March"}
   {:value 3
    :label "April"}
   {:value 4
    :label "May"}
   {:value 5
    :label "June"}
   {:value 6
    :label "July"}
   {:value 7
    :label "August"}
   {:value 8
    :label "September"}
   {:value 9
    :label "October"}
   {:value 10
    :label "November"}
   {:value 11
    :label "December"}])

(defn caption-component
  [{:keys [min-date max-date _state]} _js-props]
  (let [navigation-state (ReactDayPicker/useNavigation)

        min-month-index (dg/date-get-month min-date)
        max-month-index (dg/date-get-month max-date)
        min-year (dg/date-get-full-year min-date)
        max-year (dg/date-get-full-year max-date)

        current-month (aget navigation-state "currentMonth")
        current-month-index (dg/date-get-month current-month)
        current-year (dg/date-get-full-year current-month)
        next-month (aget navigation-state "nextMonth")
        previous-month (aget navigation-state "previousMonth")
        go-to-month (aget navigation-state "goToMonth")

        [temp-year set-temp-year] (react/useState current-year)
        [year-error set-year-error] (react/useState nil)

        change-year (fn []
                      (set-year-error nil)
                      (let [is-valid-year? (<= min-year temp-year max-year)]
                        (if is-valid-year?
                          (let [new-month (js/Date. temp-year current-month-index 1)]
                            (go-to-month new-month))
                          (let [error-reason (cond
                                               (< temp-year min-year) :year-too-small
                                               (< max-year temp-year) :year-too-big
                                               :else :unknown)]
                            (set-temp-year current-year)
                            (set-year-error error-reason)))))]

    (r/as-element
      [:div
       {:on-click      dg/stop-propagation
        :on-mouse-down dg/stop-propagation
        :on-mouse-up   dg/stop-propagation}
       [:div
        {:class "my-2 flex justify-between items-center gap-2"}
        [:div
         {:class "flex gap-2"}
         [button/square-secondary-hollow
          {:on-click   (fn []
                         (when previous-month
                           (let [year (dg/date-get-full-year previous-month)]
                             (go-to-month previous-month)
                             (set-temp-year year))))
           :class      "rounded-full"
           :tooltip    (loc/tr :date-picker/previous-month)
           :aria-label (loc/tr :date-picker/previous-month)
           :disabled?  (and (= current-year min-year)
                            (<= current-month-index min-month-index))}
          [:i.fal.fa-chevron-left]]]
        [:div
         {:class "flex items-center gap-2 z-[40000000]"}
         [:div
          {:class "w-40"}
          [select/view
           {:value     (->> month-options
                            (cu/filter-by :value current-month-index)
                            (first))
            :options   month-options
            :on-change (fn [new-month-index]
                         (let [new-month (js/Date. current-year new-month-index 1)]
                           (go-to-month new-month)))}]]
         [:div
          [:input
           {:class       (uu/cm "year-input"                ;; DON'T REMOVE - USED BY Popper
                                "h-10 px-2 text-input-light-text border border-solid border-input-light-border-paler"
                                "hover:border-input-light-border focus:border-input-light-border"
                                "bg-input-light outline-0 rounded")
            :size        "5"
            :placeholder ""
            :value       temp-year
            :on-change   (fn [e]
                           (let [value (dg/event-target-value e)
                                 value (str value)
                                 empty? (= value "")]
                             (if empty?
                               (set-temp-year "")
                               (let [parsed-year (js/parseInt value)]
                                 (when-not (js/isNaN parsed-year)
                                   (set-temp-year parsed-year))))))
            :on-blur     (fn [_e]
                           (change-year))
            :on-key-down (fn [e]
                           (dg/stop-propagation e)
                           (let [enter? (= (dg/event-key-code e) 13)]
                             (when enter?
                               (change-year))))}]]]
        [:div
         {:class "flex gap-2"}

         [button/square-secondary-hollow
          {:on-click   (fn []
                         (when next-month
                           (let [year (dg/date-get-full-year next-month)]
                             (go-to-month next-month)
                             (set-temp-year year))))
           :class      "rounded-full"
           :tooltip    (loc/tr :date-picker/next-month)
           :aria-label (loc/tr :date-picker/next-month)
           :disabled?  (and (= current-year max-year)
                            (<= max-month-index current-month-index))}
          [:i.fal.fa-chevron-right]]]]
       (when year-error
         [:div
          {:class "mt-1 ps-2 text-danger-light-text"}
          (case year-error
            :year-too-big (str "Year cannot be bigger than " max-year)
            :year-too-small (str "Year cannot be smaller than " min-year)
            "Year is not valid")])])))

(defn check-is-date-disabled
  [min-date max-date date]
  (let [min-date-ts (dg/date->millis min-date)
        max-date-ts (dg/date->millis max-date)
        date-ts (dg/date->millis date)]
    (not (<= min-date-ts date-ts max-date-ts))))

(defn date-input
  [{:keys [variant disabled? placeholder open-calendar! class
           value-date on-select-date date-format on-focus on-blur not-clearable?
           state-atom]}]
  (let [date-format (or date-format "dd/MM/yyyy")
        value (or (:temp-input @state-atom)
                  (when-not (cu/blank-value? value-date)
                    (try
                      (date-fns/format value-date date-format #js {:locale date-fns-locale/enUs})
                      (catch js/Object _err
                        (js/console.error "Failed to parse date : " value-date " << " date-format)))))]
    [:div
     {:class    (uu/cm
                  "px-3 h-10 min-w-32 w-full text-input-light-text"
                  "border border-solid border-input-light-border-paler"
                  "hover:border-input-light-border focus:border-input-light-border"
                  "invalid:border-input-light-border-accent relative rounded bg-input-light"
                  class)
      :ref      (fn [e]
                  (when (and e
                             (not= e (:input-dom @state-atom)))
                    (swap! state-atom assoc :input-dom e)))
      :disabled disabled?}
     [:div
      {:class "ps-1 flex w-full h-full items-center justify-stretch"}

      [:input
       (merge
         {:class       (uu/cm "peer py-2 w-full border-none bg-transparent p-0"
                              "placeholder-transparent focus:border-transparent"
                              "focus:outline-none focus:ring-0 text-md text-input-light-text"
                              (when-not (= variant :borderless)
                                "!pt-[6px] !pb-[6px] border border-input-light-border-paler bg-input-light hover:border-input-light-border focus:border-input-light-border")

                              "placeholder:text-base-light-text-paler")
          :on-focus    (fn [e]
                         (when (fn? on-focus)
                           (on-focus e)))
          :on-blur     (fn [e]
                         (when (fn? on-blur)
                           (on-blur e)))
          :on-change   (fn [e]
                         (swap! state-atom assoc
                                :temp-input (dg/event-target-value e)
                                :input-changes? true)
                         (deref state-atom))
          :placeholder (or placeholder date-format)
          :value       value}
         (when disabled?
           {:disabled true}))]
      (when-not disabled?
        [:div
         {:class "flex flex-row absolute top-1/2 -translate-y-1/2 right-2"}
         (when (and value-date
                    (not disabled?)
                    (not not-clearable?))
           [button/square-secondary-open
            {:tooltip    (loc/tr :date-picker/clear-date)
             :aria-label (loc/tr :date-picker/clear-date)
             :on-click   (fn []
                           (on-select-date nil))}
            [:i.fal.fa-times]])
         [button/square-secondary-open
          {:tooltip    (loc/tr :date-picker/open-calendar)
           :aria-label (loc/tr :date-picker/open-calendar)
           :on-focus   (fn [e]
                         (when (fn? on-focus)
                           (on-focus e)))
           :on-blur    (fn [_e])
           :on-click   open-calendar!}
          [:i.fal.fa-calendar]]])]]))

(defn custom-styles
  []
  {:root              "mt-2 min-h-[310px]"
   :head              "border-none text-input-light-text"
   :head_row          "h-full"
   :head_cell         "m-0 text-sm text-center align-middle h-9"
   :row               "h-full"
   :vhidden           "m-0 p-0 w-px h-px border-0 box-border [clip-path:rect(1px_1px,_1px,_1px)] appearance-none"
   :button_reset      "p-0 m-0 relative appearance-none bg-none"
   :months            "flex"
   :month             "px-4"
   :button            "border-none"
   :table             "max-w-64 m-auto border-collapse"
   :tbody             "border-0"
   :tfoot             "m-0"
   :cell              "p-1 w-9 h-9 text-center text-input-light-text"
   :caption           "p-0 flex justify-between items-center text-left"
   :caption_dropdowns "inline-flex relative"
   :caption_label     "m-0 px-1 relative z-1 inline-flex items-center border-transparent font-bold"
   :nav_button        "p-1 w-6 h-6 inline-flex justify-center items-center rounded-full"
   :week_number       "m-0 text-sm"
   :day               "h-9 w-9 max-w-9 flex justify-center items-center box-border border border-solid border-input-light-paler rounded hover:bg-base-regular"
   :day_selected      "hover:bg-input-gloomy !bg-input-gloomy !text-input-gloomy-text"
   :day_outside       "opacity-50"
   :dropdown          "p-0 m-0 appearance-none absolute t-0 l-0 b-0 r-0"
   :dropdown_icon     "ms-2"
   :dropdown_year     "relative inline-flex items-center"
   :dropdown_month    "relative inline-flex items-center"})

(defn Popper
  [js-props]
  (let [{:keys [open? toggleOpen! portalRoot setAnchor valueDate minDate maxDate onSelectDate state weekStartsOn]} (js->clj js-props :keywordize-keys true)
        {:keys [refs floatingStyles context]}
        (-> {:open                 (boolean open?)
             :onOpenChange         (fn [o e]
                                     (let [target (dg/target e)
                                           popper-dom (:popper-dom @state)
                                           child? (du/is-ancestor? target popper-dom)
                                           select-option? (du/is-ancestor? target (dg/element-by-id "react-select-menu-container"))
                                           event-type (aget e "type")
                                           ignore-event? (some #{event-type} ["focusout"])
                                           open? (or o
                                                     child?
                                                     select-option?)]
                                       (when-not ignore-event?
                                         (toggleOpen! open? e))))
             :middleware           [(offset 10)
                                    (flip #js {:fallbackAxisSideDirection "end"})
                                    (shift #js {:crossAxis true})]
             :whileElementsMounted autoUpdate}
            (clj->js)
            (useFloating)
            (js->clj :keywordize-keys true))

        click (useClick (clj->js context))
        dismiss (useDismiss (clj->js context))
        role (useRole (clj->js context))
        {:keys [getFloatingProps]} (-> [click dismiss role]
                                       (clj->js)
                                       (useInteractions)
                                       (js->clj :keywordize-keys true))
        floating-styles (js->clj floatingStyles)]
    (setAnchor (:setReference refs))
    (when open?
      (let [_floating-props (-> (getFloatingProps)
                                (js->clj :keywordize-keys true))]
        (r/as-element
          [:> FloatingPortal
           {:root portalRoot}
           [:> FloatingFocusManager
            {:context      context
             :returnFocus  false
             :initialFocus (:year-input @state)
             :modal        true}
            [:div
             (merge {:ref             (fn [r]
                                        (when (and r
                                                   (not= r (:popper-dom @state)))
                                          (swap! state assoc :popper-dom r)
                                          (let [f (:setFloating refs)
                                                input-dom (->> (.getElementsByClassName r "year-input")
                                                               (first))]
                                            (swap! state assoc :year-input input-dom)
                                            (f r))))
                     :class           "min-h-[310px] absolute z-[40000000] pointer-events-auto  top-0 left-0 bg-input-light border border-solid border-input-light-border-paler rounded"
                     :style           floating-styles

                     :aria-labelledby "test"})
             [react-day-picker
              (merge
                {:mode             "single"
                 :classNames       (custom-styles)
                 :selected         valueDate
                 :defaultMonth     valueDate
                 :onSelect         onSelectDate
                 :weekStartsOn     weekStartsOn
                 :ISOWeek          (= 1 weekStartsOn)
                 :disabled         (partial check-is-date-disabled minDate maxDate)
                 :components       {:Caption (partial caption-component
                                                      {:min-date minDate
                                                       :max-date maxDate
                                                       :state    state})}})]]]])))))

(defn format-display-value
  "Formats a UTC timestamp for display, converting to local timezone.
   Accepts an optional date-format string (date-fns format).
   Default format is from localization :date-format key."
  ([value]
   (format-display-value value nil))
  ([value date-format]
   (when-not (cu/blank-value? value)
     (let [timezone-offset (dg/date->timezone-offset (js/Date. value))
           date-ts (+ value (* timezone-offset 60000))
           date (js/Date. date-ts)]
       (date-fns/format date (or date-format (loc/tr :date-format)))))))

(defn format-value
  "Deprecated: Use format-display-value instead for configurable format.
   Kept for backwards compatibility."
  [value]
  (format-display-value value "dd-MM-yyyy"))

(defn parse-date
  [str-value _date-format]
  (edn/read-string str-value))

(defn parse-datetime
  [str-value _date-format]
  (edn/read-string str-value))

(defn date->utc-midnight
  [local-date]
  (when local-date
    (let [year (.getFullYear local-date)
          month (.getMonth local-date)
          date (.getDate local-date)
          final-date (js/Date.UTC year month date 0 0 0 0)]
      final-date)))

(defn view
  [{:keys [value _on-change _disabled? not-clearable? _placeholder _class
           min-date max-date variant _date-format _on-error! clear-error!
           on-blur on-focus
           _portal-root
           open-on-start? on-open week-starts-on]}]
  (let [state-atom (r/atom {:open? false})

        open-calendar! (fn [_]
                         (when (fn? on-focus)
                           (on-focus))
                         (swap! state-atom assoc :open? true))
        on-blur! (fn [_e]
                   (when (fn? on-blur)
                     (let [new-value (or (:utc-ts @state-atom)
                                         value)]
                       (on-blur new-value))))]
    (r/create-class
      {:component-did-mount    (fn []
                                 (when open-on-start?
                                   (swap! state-atom assoc :open? true)
                                   (when (fn? on-open)
                                     (on-open))))
       :component-will-unmount (fn [])
       :reagent-render
       (fn [{:keys [value on-change disabled? on-error! portal-root date-format] :as props}]

         (let [date-format (or date-format (loc/tr :date-format))
               min-date (or min-date (default-min-date))
               min-date-str (dates/format min-date {:pattern date-format})
               max-date (or max-date (default-max-date))
               max-date-str (dates/format max-date {:pattern date-format})
               week-starts-on (or week-starts-on (locale-week-starts-on))
               variant (or variant :outline)

               on-change (fn [value]
                           (swap! state-atom dissoc :input-changes?)
                           (when (fn? on-change)
                             (on-change value)))
               on-error! (fn [error-text]
                           (when (fn? on-error!)
                             (on-error! error-text)))
               clear-error! (fn []
                              (when (fn? clear-error!)
                                (clear-error!)))

               value-date (when value
                            (let [value (cond (number? value)
                                              value
                                              (instance? js/Date value)
                                              (dg/date->millis value)
                                              :else
                                              (js/console.log "Cannot resolve date value " (type value) " << " value))
                                  timezone-offset (dg/date->timezone-offset (js/Date. value))
                                  date-ts (+ value (* timezone-offset 60000))
                                  date (js/Date. date-ts)]
                              date))
               on-select-date (fn [& args]
                                (let [[changed-date _old-date js-opts] args
                                      opts (clj->js js-opts :keywordize-keys true)]
                                  (cond
                                    (and (nil? changed-date)
                                         (= (count args) 1))
                                    ;; handle clear
                                    (do
                                      (on-change nil)
                                      (clear-error!)

                                      (swap! state-atom dissoc :temp-input)
                                      (when-not (:keep-open? opts)
                                        (when (fn? on-blur)
                                          (on-blur nil))
                                        (swap! state-atom assoc :open? false)))

                                    (not changed-date)
                                    ;; handle no change
                                    (when-not (:keep-open? opts)
                                      (when (fn? on-blur)
                                        (on-blur nil))
                                      (clear-error!)
                                      (swap! state-atom assoc :open? false)
                                      (deref state-atom))

                                    :else
                                    (let [utc-ts (date->utc-midnight changed-date)]
                                      (swap! state-atom assoc :utc-ts utc-ts)
                                      (on-change utc-ts)
                                      (when-not (:keep-open? opts)
                                        (swap! state-atom assoc :open? false)
                                        (on-blur! nil))
                                      (swap! state-atom dissoc :temp-input)
                                      (clear-error!)))))

               on-blur-input (fn [_e]
                               (when (:input-changes? @state-atom)
                                 (let [temp-input (-> @state-atom
                                                      :temp-input
                                                      cu/str-trim)]
                                   (if (cu/empty-value? temp-input)
                                     (do (clear-error!)
                                         (on-select-date nil))
                                     (let [parsed-date (date-fns/parse temp-input date-format (js/Date.) #js {:locale date-fns-locale/enUs})
                                           invalid-date? (not (date-fns/isValid parsed-date))]
                                       (if invalid-date?
                                         (on-error! (loc/tr :date-input-error/invalid-date-string))
                                         (do
                                           (clear-error!)
                                           (cond
                                             (< parsed-date min-date)
                                             (on-error! (loc/tr :date-input-error/cannot-be-smaller-than [min-date-str]))

                                             (< max-date parsed-date)
                                             (on-error! (loc/tr :date-input-error/cannot-be-larger-than [max-date-str]))

                                             :else
                                             (on-select-date parsed-date))))))))

                               (when (fn? on-blur)
                                 (on-blur nil)))]
           [:div
            {:class           (uu/cm "peer"
                                     (when-not (cu/blank-value? value)
                                       "value"))
             ;; Prevent these so date picker works with old editable grid
             :on-click        dg/stop-propagation
             :on-double-click dg/stop-propagation
             :on-mouse-move   dg/stop-propagation
             :on-mouse-enter  dg/stop-propagation
             :on-mouse-leave  dg/stop-propagation
             :on-mouse-down   dg/stop-propagation
             :on-mouse-up     dg/stop-propagation
             :on-mouse-over   dg/stop-propagation
             :on-mouse-out    dg/stop-propagation
             :ref             (fn [r]
                                (when (and r
                                           (not (:ref-set? @state-atom)))
                                  (swap! state-atom assoc :ref-set? true)
                                  (if-let [set-ref! (:set-ref! @state-atom)]
                                    (set-ref! r)
                                    (swap! state-atom assoc :dom-ref r))))}
            (when-not disabled?
              [:> Popper
               {:open?         (:open? @state-atom)
                :state         state-atom
                :portal-root   portal-root
                :toggle-open!  (fn [o e]
                                 (swap! state-atom assoc :open? o)
                                 (when (fn? on-focus)
                                   (on-focus e)))
                :set-anchor    (fn [setRef]
                                 (when-not (:anchor-set? @state-atom)
                                   (swap! state-atom assoc :anchor-set? true)
                                   (if-let [dom-ref (:dom-ref @state-atom)]
                                     (setRef dom-ref)
                                     (swap! state-atom assoc :set-ref! setRef))))
                :valueDate     value-date
                :minDate       min-date
                :maxDate       max-date
                :onSelectDate  on-select-date
                :weekStartsOn  week-starts-on
                :notClearable? not-clearable?}])
            [date-input
             (merge
               props
               {:variant        variant
                :on-blur        on-blur-input
                :value-date     value-date
                :state-atom     state-atom
                :date-format    date-format
                :open-calendar! open-calendar!
                :on-select-date on-select-date})]]))})))
